import React from 'react';
import { Code2, Database, ExternalLink, Layout } from 'lucide-react';

import { Product } from '../types';

interface PortfolioProps {
  products: Product[];
}

type ProductAccent = {
  ext: string;
  barClassName: string;
  iconClassName: string;
  Icon: typeof Code2;
};

const productAccents: Record<string, ProductAccent> = {
  'cebu-port': { ext: '.sln', barClassName: 'bg-dotnet', iconClassName: 'text-dotnet', Icon: Database },
  loggle: { ext: '.ts', barClassName: 'bg-js', iconClassName: 'text-amber-600', Icon: Layout },
  sqloom: { ext: '.cs', barClassName: 'bg-ts', iconClassName: 'text-ts', Icon: Code2 },
};

const toMethodName = (value: string) =>
  value
    .trim()
    .replace(/[^\w\s]/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join('');

export const Portfolio: React.FC<PortfolioProps> = ({ products }) => {
  return (
    <section id="work" className="py-32 bg-surface relative">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between mb-16">
          <div>
            <span className="text-syntaxComment font-mono text-sm font-medium">{`// 0 references | ${products.length} changes`}</span>
            <h2 className="font-display text-4xl font-bold text-gray-900 mt-1">Solution Explorer</h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {products.map((product) => {
            const accent = productAccents[product.id] ?? {
              ext: '.tsx',
              barClassName: 'bg-dotnet',
              iconClassName: 'text-dotnet',
              Icon: Code2,
            };
            const filename = toMethodName(product.name);
            const action = toMethodName(product.ctaText || 'View');
            const Icon = accent.Icon;

            return (
              <div
                key={product.id}
                className="group bg-white border border-gray-200 hover:border-gray-300 hover:shadow-xl transition-all duration-300 p-6 flex flex-col h-full rounded-sm relative overflow-hidden shadow-sm"
              >
                <div
                  className={`absolute top-0 left-0 w-1 h-full ${accent.barClassName} opacity-80 group-hover:opacity-100 transition-opacity`}
                />

                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-gray-50 rounded-sm border border-gray-100">
                    <Icon className={accent.iconClassName} size={20} />
                  </div>
                  <div>
                    <h3 className="text-gray-900 font-mono text-lg flex items-center gap-1 font-semibold">
                      {filename}
                      <span className="text-gray-400 font-normal">{accent.ext}</span>
                    </h3>
                    <p className="text-xs text-gray-500 font-mono">{product.tagline}</p>
                  </div>
                </div>

                <p className="text-gray-600 mb-8 flex-grow leading-relaxed font-sans">{product.description}</p>

                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {product.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-[10px] font-mono px-2 py-1 bg-gray-50 text-gray-700 border border-gray-200 rounded-sm font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <a
                    href={product.ctaLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-syntaxFunction hover:text-black transition-colors font-mono mt-2 font-medium"
                  >
                    <span>{action}()</span>
                    <ExternalLink size={14} />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
